// FIX-CCS-D-OFFLINE-ROUTES (SD-O64, Adeel ruling 2026-10-06): a screen whose code could not be loaded — no signal and
// the chunk is not on the phone, or a dropped fetch — must never show the raw error or a dead screen. The farmer keeps
// the shell (top bar, bottom nav), reads "No signal — this screen isn't saved on this phone yet…" and can Retry.
// Any other error is re-thrown to the app-level ErrorBoundary unchanged.

import React, { Component, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CloudOff, RotateCw } from 'lucide-react';
import { Screen } from '@/components/layout/Screen';
import { Button } from '@/shared/ui/button';

/** True for the errors a failed lazy route import produces (Vite/Chrome/Safari/Firefox wordings) or any error while offline. */
export function isChunkLoadError(error: unknown): boolean {
  const msg = error instanceof Error ? error.message : String(error ?? '');
  if (/dynamically imported module|Importing a module script failed|Loading chunk|Failed to fetch|Load failed|ChunkLoadError|error loading dynamically imported module/i.test(msg)) return true;
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

export const NoSignalScreen: React.FC<{ onRetry: () => void }> = ({ onRetry }) => {
  const { t } = useTranslation();
  return (
    <Screen showTopBar={true} showBottomNav={true}>
      <div data-testid="screen-not-saved" role="status" className="flex flex-col items-center justify-center gap-4 p-6 min-h-[50vh] text-center">
        <CloudOff className="h-12 w-12 text-muted-foreground" />
        <p className="text-sm text-foreground max-w-xs">{t('outbox.screenNotSaved')}</p>
        <Button variant="outline" size="sm" onClick={onRetry}>
          <RotateCw className="mr-1 h-4 w-4" />
          {t('common.retry', 'Try again')}
        </Button>
      </div>
    </Screen>
  );
};

interface State {
  error: Error | null;
}

class Boundary extends Component<{ children: ReactNode; resetKey: string }, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidUpdate(prev: { resetKey: string }) {
    // A navigation to another screen clears the failure of the previous one.
    if (prev.resetKey !== this.props.resetKey && this.state.error) this.setState({ error: null });
  }

  retry = () => {
    // React.lazy remembers a rejected import for the life of the page: the only clean retry is a reload, which the
    // service worker serves from the cached shell even with no signal.
    window.location.reload();
  };

  render() {
    const { error } = this.state;
    if (error) {
      if (isChunkLoadError(error)) return <NoSignalScreen onRetry={this.retry} />;
      throw error; // not ours — the app-level ErrorBoundary shows its screen
    }
    return this.props.children;
  }
}

/** Wrap the route tree once; the key resets the boundary on navigation. */
export const OfflineRouteBoundary: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { pathname } = useLocation();
  return <Boundary resetKey={pathname}>{children}</Boundary>;
};
